import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface SitesPageFilters {
  paginationState: { pageIndex: number; pageSize: number };
  sortingState: { desc: boolean; id: string }[];
  totCount: number;
  pageCount: number;
  search?: string;
}

export interface DevicePageFilters {
  paginationState: { pageIndex: number; pageSize: number };
  sortingState: { desc: boolean; id: string }[];
  totCount: number;
  pageCount: number;
  classId: string;
  model: any;
  status?: string;
  installed?: string;
  search?: string;
  snrs?: string[];
}

export interface AlertsPageFilters {
  paginationState: { pageIndex: number; pageSize: number };
  sortingState: { desc: boolean; id: string }[];
  totCount: number;
  pageCount: number;
  state?: string;
  classes?: string[];
  search?: string;
  snrs?: string[];
}

export interface AuditTrailPageFilters {
  paginationState: { pageIndex: number; pageSize: number };
  sortingState: { desc: boolean; id: string }[];
  totCount: number;
  pageCount: number;
  search?: string;
  username?: string;
  action?: string;
  reference?: string;
  snr?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface UsersPageFilters {
  paginationState: { pageIndex: number; pageSize: number };
  sortingState: { desc: boolean; id: string }[];
  totCount: number;
  pageCount: number;
  search?: string;
  roles?: string;
  classes?: string;
}

type DashboardAction = {
  deviceCount: any;
  zeroCount: any;
  devicesDown: any;
  leaking: any;
  weakSignal: any;
  water: any;
  electricity: any;
  temperature: any;
  level: any;
  gas: any;
};

type DashboardOverview = {
  data: any;
  counts: any;
};

interface PageState {
  devicePageFilters: DevicePageFilters;
  issuesPageFilters: DevicePageFilters;
  dashboardOverview: DashboardOverview;
  dashboardActions: DashboardAction;
  auditTrailPageFilters: AuditTrailPageFilters;
  alertsPageFilters: AlertsPageFilters;
  sitesPageFilters: SitesPageFilters;
  usersPageFilters: UsersPageFilters;
}

const initialState: PageState = {
  devicePageFilters: {
    paginationState: { pageIndex: 0, pageSize: 10 },
    sortingState: [{ desc: true, id: "name" }],
    totCount: 0,
    pageCount: 0,
    classId: "",
    model: "",
    status: undefined,
    installed: undefined,
    search: "",
  },
  issuesPageFilters: {
    paginationState: { pageIndex: 0, pageSize: 10 },
    sortingState: [{ desc: true, id: "name" }],
    totCount: 0,
    pageCount: 0,
    classId: "",
    model: "",
    status: undefined,
    installed: undefined,
    search: "",
    snrs: [],
  },
  dashboardOverview: {
    data: undefined,
    counts: undefined,
  },
  dashboardActions: {
    deviceCount: undefined,
    zeroCount: undefined,
    devicesDown: undefined,
    leaking: undefined,
    weakSignal: undefined,
    water: undefined,
    electricity: undefined,
    temperature: undefined,
    level: undefined,
    gas: undefined,
  },
  auditTrailPageFilters: {
    paginationState: {
      pageIndex: 0,
      pageSize: 10,
    },
    sortingState: [{ desc: true, id: "created" }],
    totCount: 0,
    pageCount: 0,
  },
  alertsPageFilters: {
    paginationState: { pageIndex: 0, pageSize: 10 },
    sortingState: [{ desc: true, id: "created" }],
    totCount: 0,
    pageCount: 0,
  },
  sitesPageFilters: {
    paginationState: { pageIndex: 0, pageSize: 10 },
    sortingState: [{ desc: false, id: "name" }],
    totCount: 0,
    pageCount: 0,
    search: "",
  },
  usersPageFilters: {
    paginationState: { pageIndex: 0, pageSize: 10 },
    sortingState: [{ desc: true, id: "name" }],
    totCount: 0,
    pageCount: 0,
    search: "",
    roles: "",
    classes: "",
  },
};

const pageSlice = createSlice({
  name: "page",
  initialState,
  reducers: {
    setDevicePageFilters: (state, action: PayloadAction<DevicePageFilters>) => {
      state.devicePageFilters = action.payload;
    },
    setIssuesPageFilters: (state, action: PayloadAction<DevicePageFilters>) => {
      state.issuesPageFilters = action.payload;
    },
    setDashboardOverview: (state, action: PayloadAction<DashboardOverview>) => {
      state.dashboardOverview = action.payload;
    },
    setDashboardActions: (state, action: PayloadAction<DashboardAction>) => {
      state.dashboardActions = action.payload;
    },
    setAuditTrailPageFilters: (
      state,
      action: PayloadAction<AuditTrailPageFilters>
    ) => {
      state.auditTrailPageFilters = action.payload;
    },
    setAlertsPageFilters: (state, action: PayloadAction<AlertsPageFilters>) => {
      state.alertsPageFilters = action.payload;
    },
    setSitesPageFilters: (state, action: PayloadAction<SitesPageFilters>) => {
      state.sitesPageFilters = action.payload;
    },
    setUsersPageFilters: (state, action: PayloadAction<UsersPageFilters>) => {
      state.usersPageFilters = action.payload;
    },
    resetPage: () => initialState,
  },
});

export const {
  setDevicePageFilters,
  setDashboardOverview,
  setDashboardActions,
  setIssuesPageFilters,
  setAuditTrailPageFilters,
  setAlertsPageFilters,
  resetPage,
  setSitesPageFilters,
  setUsersPageFilters,
} = pageSlice.actions;
export default pageSlice.reducer;
